import type { ThemeConfig } from 'antd'
import { colors, typography, shadows, spacingNum } from './design-tokens'

/**
 * AVA Platform Ant Design Theme Configuration
 *
 * This theme configuration maps our design tokens to Ant Design's token system,
 * ensuring consistency across all components.
 */
export const antdTheme: ThemeConfig = {
  token: {
    // Typography
    fontFamily: typography.fontFamily.body,
    fontSize: typography.fontSizeNum.base,
    fontSizeHeading1: typography.fontSizeNum['4xl'],
    fontSizeHeading2: typography.fontSizeNum['3xl'],
    fontSizeHeading3: typography.fontSizeNum['2xl'],
    fontSizeHeading4: typography.fontSizeNum.xl,
    fontSizeHeading5: typography.fontSizeNum.lg,

    // Brand colors
    colorPrimary: colors.primary[500],
    colorSuccess: colors.success[500],
    colorWarning: colors.warning[500],
    colorError: colors.error[500],
    colorInfo: colors.info[500],

    // Text colors
    colorText: colors.neutral[950],
    colorTextSecondary: colors.neutral[600],
    colorTextTertiary: colors.neutral[500],
    colorTextQuaternary: colors.neutral[400],

    // Background colors
    colorBgContainer: '#FFFFFF',
    colorBgLayout: colors.neutral[50],
    colorBgElevated: '#FFFFFF',
    colorBgSpotlight: colors.neutral[100],

    // Border colors
    colorBorder: colors.neutral[200],
    colorBorderSecondary: colors.neutral[100],

    // Border radius (zero for AVA design)
    borderRadius: 0,
    borderRadiusLG: 0,
    borderRadiusSM: 0,
    borderRadiusXS: 0,

    // Spacing (using spacingNum for Ant Design compatibility)
    padding: spacingNum.lg,
    paddingLG: spacingNum.xl,
    paddingSM: spacingNum.md,
    paddingXS: spacingNum.sm,
    paddingXXS: spacingNum.xs,
    margin: spacingNum.lg,
    marginLG: spacingNum.xl,
    marginSM: spacingNum.md,
    marginXS: spacingNum.sm,
    marginXXS: spacingNum.xs,

    // Component sizing
    controlHeight: 40,
    controlHeightLG: 48,
    controlHeightSM: 32,
    controlHeightXS: 24,

    // Shadows
    boxShadow: shadows.base,
    boxShadowSecondary: shadows.sm,
    boxShadowTertiary: shadows.md,

    // Line height
    lineHeight: typography.lineHeight.normal,
  },

  components: {
    Button: {
      borderRadius: spacingNum.xxs,
      borderRadiusLG: spacingNum.xxs,
      borderRadiusSM: spacingNum.xxs,

      // Primary button
      colorPrimary: colors.primary[500],
      colorPrimaryHover: colors.primary[400],
      colorPrimaryActive: colors.primary[600],
      primaryShadow: 'none',

      // Default/Secondary button - using light blue (primary[100])
      defaultBorderColor: colors.primary[100],
      defaultColor: colors.primary[100],
      defaultBg: 'transparent',
      defaultHoverBg: colors.primary[50],
      defaultHoverBorderColor: colors.primary[200],
      defaultHoverColor: colors.primary[200],
      defaultActiveBg: colors.primary[50],
      defaultActiveBorderColor: colors.primary[300],
      defaultActiveColor: colors.primary[300],

      // Ghost button
      ghostBg: 'transparent',

      // Text button
      textHoverBg: 'transparent',

      // Sizing
      controlHeight: 40,
      controlHeightLG: 48,
      controlHeightSM: 32,

      // Horizontal padding: small=12px, medium=16px, large=24px
      // Using paddingInline tokens for direct control over button padding
      paddingInline: spacingNum.xl,           // 24px for medium
      paddingInlineLG: spacingNum['2xl'],     // 32px for large
      paddingInlineSM: spacingNum.lg,         // 16px for small

      // Content horizontal padding (used in internal calculations)
      paddingContentHorizontal: spacingNum.xl,
      paddingContentHorizontalLG: spacingNum['2xl'],
      paddingContentHorizontalSM: spacingNum.lg,

      // Font sizes for button text
      contentFontSize: typography.fontSizeNum.base,       // 16px for medium (default)
      contentFontSizeLG: typography.fontSizeNum.lg,       // 18px for large
      contentFontSizeSM: typography.fontSizeNum.sm,       // 14px for small
      fontWeight: typography.fontWeight.normal,
    },

    Input: {
      borderRadius: 0,
      borderRadiusLG: 0,
      borderRadiusSM: 0,

      // Colors
      colorBorder: colors.neutral[300],
      colorBgContainer: '#FFFFFF',
      colorText: colors.neutral[950],
      colorTextPlaceholder: colors.neutral[400],

      // Hover & Focus
      hoverBorderColor: colors.primary[400],
      activeBorderColor: colors.primary[500],
      activeShadow: `0 0 0 2px ${colors.primary[50]}`,

      // Sizing
      controlHeight: 40,
      controlHeightLG: 48,
      controlHeightSM: 32,
      paddingBlock: 4,
      paddingInline: 8,
    },

    InputNumber: {
      borderRadius: 0,
      borderRadiusLG: 0,
      borderRadiusSM: 0,
      controlHeight: 40,
      controlHeightLG: 48,
      controlHeightSM: 32,
      paddingBlock: 4,
      paddingInline: 8,
    },

    Select: {
      borderRadius: 0,
      borderRadiusLG: 0,
      borderRadiusSM: 0,
      controlHeight: 40,
      controlHeightLG: 48,
      controlHeightSM: 32,

      // Colors
      colorBorder: colors.neutral[300],
      optionSelectedBg: colors.primary[50],
      optionSelectedColor: colors.primary[500],
      optionActiveBg: colors.neutral[100],
    },

    Checkbox: {
      borderRadius: 0,
      borderRadiusLG: 0,
      borderRadiusSM: 0,
      colorPrimary: colors.primary[500],
      colorPrimaryHover: colors.primary[400],
      // Font size to match small inputs
      fontSize: typography.fontSizeNum.sm,
    },

    Radio: {
      colorPrimary: colors.primary[500],
      colorPrimaryHover: colors.primary[400],
      // Font size to match small inputs
      fontSize: typography.fontSizeNum.sm,
    },

    Switch: {
      colorPrimary: colors.primary[500],
      colorPrimaryHover: colors.primary[400],
    },

    Card: {
      borderRadius: 0,
      borderRadiusLG: 0,
      paddingLG: 24,
      headerFontSize: typography.fontSizeNum.lg,
      boxShadow: shadows.sm,
      boxShadowTertiary: shadows.base,
    },

    Drawer: {
      borderRadius: 0,
      borderRadiusLG: 0,
      paddingLG: 24,
      footerPaddingBlock: 16,
      footerPaddingInline: 24,
    },

    Modal: {
      borderRadius: 0,
      borderRadiusLG: 0,
      paddingLG: 24,
      headerBg: '#FFFFFF',
      footerBg: '#FFFFFF',
    },

    Table: {
      borderRadius: 0,
      borderRadiusLG: 0,

      // Background - transparent to inherit from parent
      colorBgContainer: 'transparent',
      footerBg: 'transparent',

      // Header
      headerBg: 'transparent',
      headerColor: colors.neutral[950],
      headerSortActiveBg: colors.neutral[100],
      headerSortHoverBg: colors.neutral[100],

      // Row
      rowHoverBg: 'transparent',
      rowSelectedBg: colors.primary[50],
      rowSelectedHoverBg: colors.primary[50],

      // Border
      borderColor: colors.neutral[200],
    },

    Tabs: {
      borderRadius: 0,

      // Colors - using primary[100] (#4277BB) for active/selected state
      itemActiveColor: colors.primary[100],
      itemSelectedColor: colors.primary[100],
      itemHoverColor: colors.primary[200],
      inkBarColor: colors.primary[100],

      // Typography
      titleFontSize: typography.fontSizeNum.base,
      titleFontSizeLG: typography.fontSizeNum.lg,
      titleFontSizeSM: typography.fontSizeNum.sm,
    },

    Tag: {
      borderRadius: 0,
      borderRadiusLG: 0,
      borderRadiusSM: 0,
      defaultBg: colors.neutral[100],
      defaultColor: colors.neutral[700],
    },

    Badge: {
      colorError: colors.error[500],
      colorSuccess: colors.success[500],
      colorInfo: colors.info[500],
      colorWarning: colors.warning[500],
    },

    Form: {
      labelFontSize: typography.fontSizeNum.sm,
      labelColor: colors.neutral[700],
      labelRequiredMarkColor: colors.error[500],
      itemMarginBottom: 24,
    },

    Pagination: {
      // Remove borders and backgrounds for cleaner look
      borderRadius: 0,
      itemBg: 'transparent',
      itemActiveBg: 'transparent',
      itemActiveBgDisabled: 'transparent',

      // Sizing
      itemSize: 32,
      itemSizeSM: 32,
      controlHeightSM: 32,

      // Colors - text only, no backgrounds
      colorPrimary: colors.neutral[950],
      colorPrimaryHover: colors.primary[100],
      colorText: colors.neutral[600],
      colorTextDisabled: colors.neutral[300],

      // Remove item border
      colorBorder: 'transparent',
    },

    Breadcrumb: {
      linkColor: colors.neutral[600],
      linkHoverColor: colors.primary[500],
      itemColor: colors.neutral[950],
      separatorColor: colors.neutral[400],
    },

    Menu: {
      borderRadius: 0,
      itemBorderRadius: 0,
      itemBg: 'transparent',
      itemSelectedBg: colors.primary[700],
      itemSelectedColor: '#FFFFFF',
      itemHoverBg: colors.primary[600],
      itemHoverColor: '#FFFFFF',
      itemActiveBg: colors.primary[600],
      darkItemBg: colors.primary[700],
      darkItemSelectedBg: colors.primary[600],
      darkItemHoverBg: colors.primary[600],
    },

    Layout: {
      headerBg: '#FFFFFF',
      headerHeight: 64,
      headerPadding: '0 24px',
      siderBg: colors.primary[700],
      bodyBg: colors.neutral[50],
      footerBg: '#FFFFFF',
    },

    Tooltip: {
      borderRadius: 2,
      colorBgSpotlight: colors.neutral[800],
      fontSize: 12,
      controlHeight: 24,
    },

    Popover: {
      borderRadius: 0,
      borderRadiusLG: 0,
      borderRadiusSM: 0,
    },

    Dropdown: {
      borderRadius: 0,
      borderRadiusLG: 0,
      borderRadiusSM: 0,
    },

    DatePicker: {
      borderRadius: 0,
      borderRadiusLG: 0,
      borderRadiusSM: 0,
      controlHeight: 40,
      controlHeightLG: 48,
      controlHeightSM: 32,
      // Smaller calendar cells
      cellWidth: 28,
      cellHeight: 22,
      // Smaller fonts
      fontSize: typography.fontSizeNum.sm,
    },

    TimePicker: {
      borderRadius: 0,
      borderRadiusLG: 0,
      borderRadiusSM: 0,
      controlHeight: 40,
      controlHeightLG: 48,
      controlHeightSM: 32,
    },

    Message: {
      contentBg: '#FFFFFF',
      borderRadiusLG: 2,
    },

    Notification: {
      borderRadius: 0,
      width: 384,
    },
  },
}
